/**
 * Helpers for working with job definition files.
 */
import {
  constString,
  opArtifactMembershipArtifactVersion,
  opArtifactMembershipForAlias,
  opArtifactVersionFile,
  opArtifactVersionMetadata,
  opFileContents,
  opProjectArtifact,
  opRootProject,
} from '@wandb/weave/core';

import {extractConfig} from '../utils';
import {
  JobMetadataInputTypes,
  JsonObject,
  METADATA_FILES_KEY,
  METADATA_RUNCONFIG_KEY,
} from '../utilTypes';

// Get the contents of the wandb-job.json file for a specific Job version.
export const getJobDefinitionFileOp = ({
  entityName,
  projectName,
  jobName,
  alias,
}: {
  entityName: string;
  projectName: string;
  jobName: string;
  alias: string;
}) => {
  const project = opRootProject({
    entityName: constString(entityName),
    projectName: constString(projectName),
  });
  const artifact = opProjectArtifact({
    project,
    artifactName: constString(jobName),
  });
  const artifactMembership = opArtifactMembershipForAlias({
    artifact,
    aliasName: constString(alias),
  });
  const artifactVersion = opArtifactMembershipArtifactVersion({
    artifactMembership,
    aliasName: constString(alias),
  });
  const file = opArtifactVersionFile({
    artifactVersion,
    path: constString('wandb-job.json'),
  });
  return opFileContents({file});
};

export const getJobMetadataOp = ({
  entityName,
  projectName,
  jobName,
  alias,
}: {
  entityName: string;
  projectName: string;
  jobName: string;
  alias: string;
}) => {
  const project = opRootProject({
    entityName: constString(entityName),
    projectName: constString(projectName),
  });
  const artifact = opProjectArtifact({
    project,
    artifactName: constString(jobName),
  });
  const artifactMembership = opArtifactMembershipForAlias({
    artifact,
    aliasName: constString(alias),
  });
  const artifactVersion = opArtifactMembershipArtifactVersion({
    artifactMembership,
    aliasName: constString(alias),
  });
  const artifaactMetadata = opArtifactVersionMetadata({artifactVersion});
  return artifaactMetadata;
};

const getExampleValue = (type: string): any => {
  if (type === 'string') {
    return '';
  }
  if (type === 'boolean') {
    return false;
  }
  if (type === 'number') {
    return 0;
  }
  if (type === 'list') {
    return [];
  }
  if (type === 'none') {
    return null;
  }
  if (type === 'artifactVersion') {
    return 'wandb-artifact://<ENTITY_NAME>/<PROJECT_NAME>/<ARTIFACT_COLLECTION_NAME>:<ALIAS_OR_VERSION>';
  }
  console.error("Don't know how to getExampleValue for " + type);
  return null;
};

const isUntypedDict = (input: any): boolean => {
  // when using the launch manage functions, there is no wb_type
  // on the top level keys (files, @wandb.config, etc.)
  // so we need to check if the object is an untyped dict
  return (
    typeof input === 'object' && input.wb_type == null && !Array.isArray(input)
  );
};

const getTypeMapFromDict = (dict: any): any => {
  // launch manage functions don't nest under the params
  // key so need to check for returning just a type_map directly
  //
  if (dict.params != null && dict.params.type_map != null) {
    return dict.params.type_map;
  } else if (dict.type_map != null) {
    return dict.type_map;
  } else {
    return dict;
  }
};

export const typedDictToDict = (
  typedDict: any,
  useExampleValue: boolean
): JsonObject => {
  const dict: JsonObject = {};
  const typeMap = getTypeMapFromDict(typedDict);
  const keys = Object.keys(typeMap);
  for (const key of keys) {
    const type = typeMap[key].wb_type;
    if (type === 'typedDict' || (type == null && isUntypedDict(typeMap[key]))) {
      dict[key] = typedDictToDict(typeMap[key], useExampleValue);
    } else {
      dict[key] = useExampleValue ? getExampleValue(type) : type;
    }
  }
  return dict;
};

function isJsonObject(obj: any): obj is JsonObject {
  if (obj == null) {
    return false;
  }
  return (
    obj.wb_type === 'typedDict' ||
    (typeof obj === 'object' && !Array.isArray(obj))
  );
}

// To improve readability of working with Job files.
export class JobFile {
  spec: JsonObject;
  // metadataInputTypes is a dictionary that contains the input_types
  // as of SDK 0.17.0 we started allowing users to define input_types
  // in the job metadata. When this is present, ignore the spec
  // input types and use the metadata input types
  metadataInputTypes?: JobMetadataInputTypes;

  constructor(filecontents: string, metadata?: JsonObject) {
    this.spec = JSON.parse(filecontents);
    this.metadataInputTypes = metadata?.input_types as JobMetadataInputTypes;
  }

  pruneFileContents(fileName: string, fileContents: JsonObject): JsonObject {
    if (this.metadataInputTypes == null) {
      return {};
    }
    const files = this.metadataInputTypes[METADATA_FILES_KEY];
    if (files == null) {
      return {};
    }
    const specifiedKeys = files[fileName];
    if (specifiedKeys == null) {
      return {};
    }
    const prunedContents = this.orderConfig(
      fileContents,
      typedDictToDict(specifiedKeys, false)
    );
    return prunedContents;
  }

  getConfigInputExample(): JsonObject {
    // if metadataInputTypes is defined in metadata, then
    // example should only contain keys specified from the
    // metadataInputTypes on the metadata
    if (this.metadataInputTypes != null) {
      const runConfigKeys = this.metadataInputTypes[METADATA_RUNCONFIG_KEY];
      if (runConfigKeys == null) {
        return {};
      }
      return typedDictToDict(runConfigKeys, true);
    }
    return typedDictToDict(this.spec.input_types, true);
  }

  // The input_types in wandb-job.json have preserved the original
  // config order, while the raw config values for the run might be
  // being alphabetized by the SDK and sorted by key length somewhere else.
  // Users might have intentionally ordered their config (e.g. by importance)
  // so we attempt to recreate it here.
  // TODO: Recursively ordering dict values?
  orderConfig(config: JsonObject, specifiedKeys: JsonObject): JsonObject {
    const ordered: JsonObject = {};
    for (const key of Object.keys(specifiedKeys)) {
      if (key in config) {
        const configValue = config[key];
        const specifiedKeyValue = specifiedKeys[key];
        if (isJsonObject(specifiedKeyValue) && isJsonObject(configValue)) {
          ordered[key] = this.orderConfig(configValue, specifiedKeyValue);
        } else {
          ordered[key] = configValue;
        }
      }
    }
    // when users are using metadata, they are using it to parse
    // down the set of keys defined for the config, so don't fill
    // in rest of the keys
    if (this.metadataInputTypes != null) {
      return ordered;
    }
    // fill in the rest of the keys
    for (const [key, value] of Object.entries(config)) {
      if (!(key in specifiedKeys)) {
        ordered[key] = value;
      }
    }
    return ordered;
  }

  getOrderedClonedRunConfig(runConfig: JsonObject): JsonObject {
    const config = extractConfig(runConfig);
    const orderedConfig = this.orderConfig(
      config,
      this.getConfigInputExample()
    );
    return orderedConfig;
  }

  getPrunedFilesMap(filesMap: {[x: string]: any}) {
    const prunedFilesMap = Object.keys(filesMap).reduce((acc, key) => {
      const prunedContents = this.pruneFileContents(key, filesMap[key]);
      if (Object.keys(prunedContents).length > 0) {
        acc[key] = prunedContents;
      }
      return acc;
    }, {} as {[x: string]: JsonObject});
    return prunedFilesMap;
  }
}
